import React, { useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';

// Styled Components
const Body = styled.div`
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  background: #fff;
  padding: 2rem;
    box-shadow: 0 0 10px rgba(169, 169, 169, 1.0);
  border-radius: 10px;
  width: 100%;
  max-width: 1500px;
  text-align: center;
`;

const Heading = styled.h1`
  color: #333;
`;

const Subheading = styled.h2`
  color: #333;
`;

const Input = styled.input`
  width: 90%;
  padding: 0.5rem;
  margin: 1rem 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-align: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background: #007BFF;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #0056b3;
  }
`;

const ErrorMessage = styled.p`
  color: red;
`;

const Text = styled.p`
  color: #555;

  strong {
    color: #000;
  }
`;

const List = styled.ul`
  list-style-type: none;
  padding: 0;
`;

const ListItem = styled.li`
  background: #e9ecef;
  margin: 0.5rem 0;
  padding: 0.5rem;
  border-radius: 4px;
`;

function WhoisLookup() {
    const [domain, setDomain] = useState('');
    const [whoisData, setWhoisData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleDomainChange = (e) => {
        setDomain(e.target.value);
    };

    const handleLookup = async () => {
        if (!domain) return;
        setLoading(true);
        setError('');
        setWhoisData(null);

        try {
            const domainExtension = domain.split('.').pop();
            let url = '';

            if (domainExtension === 'com') {
                url = `https://rdap.verisign.com/com/v1/domain/${domain}`;
            } else if (domainExtension === 'net') {
                url = `https://rdap.verisign.com/net/v1/domain/${domain}`;
            } else if (domainExtension === 'ar') {
                url = `https://rdap.nic.ar/domain/${domain}`;
            } else if (domainExtension === 'org') {
                url = `https://rdap.publicinterestregistry.org/rdap/domain/${domain}`;
            } else if (domainExtension === 'info') {
                url = `https://rdap.donuts.co/rdap/domain/${domain}`;
            } else if (domainExtension === 'biz') {
                url = `https://rdap.nic.biz/domain/${domain}`;
            } else if (domainExtension === 'pro') {
                url = `https://rdap.donuts.co/rdap/domain/${domain}`;
            } else if (domainExtension === 'beer') {
                url = `https://rdap.nic.beer/domain/${domain}`;
            } else if (domainExtension === 'tv') {
                url = `https://rdap.nic.tv/domain/${domain}`;
            } else if (domainExtension === 'br') {
                url = `https://rdap.registro.br/domain/${domain}`;
            } else if (domainExtension === 'name') {
                url = `https://rdapserver.net/domain/${domain}`;
            } else {
                setError('Extensión de dominio no soportada.');
                setLoading(false);
                return;
            }

            const response = await axios.get(url);
            setWhoisData(response.data);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setError('Dominio no encontrado.');
            } else {
                setError('Dominio no encontrado.(*)');
            }
        } finally {
            setLoading(false);
        }
    };

    const extractRegistrarData = (entities) => {
        const registrar = entities.find(entity => entity.roles && entity.roles.includes('registrar'));
        if (!registrar) return {};

        const registrarName = registrar.vcardArray?.[1]?.find(item => item[0] === 'fn')?.[3] || 'No disponible';
        const ianaId = registrar.publicIds?.find(id => id.type === 'IANA Registrar ID')?.identifier || 'No disponible';

        return { registrarName, ianaId };
    };

    const extractAbuseContact = (entities) => {
        const registrar = entities.find(entity => entity.roles && entity.roles.includes('registrar'));
        if (!registrar) return {};

        const abuseContact = registrar.entities?.find(entity => entity.roles && entity.roles.includes('abuse'));
        if (!abuseContact) return {};

        const abusePhone = abuseContact.vcardArray?.[1]?.find(item => item[0] === 'tel')?.[3] || 'No disponible';
        const abuseEmail = abuseContact.vcardArray?.[1]?.find(item => item[0] === 'email')?.[3] || 'No disponible';

        return { abusePhone, abuseEmail };
    };

    return (
        <Body>
            <Container>
                <Heading>Consulta WHOIS</Heading>
                <Input
                    type="text"
                    value={domain}
                    onChange={handleDomainChange}
                    placeholder="Dominio.com"
                />
                <Button onClick={handleLookup} disabled={loading}>
                    {loading ? 'Consultando...' : 'Consultar WHOIS'}
                </Button>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {whoisData && (
                    <div>
                        <Subheading>Resultados WHOIS</Subheading>
                        <Text><strong>Dominio:</strong> {whoisData.ldhName}</Text>
                        <Text><strong>Estado:</strong> {whoisData.status ? whoisData.status.join(', ') : 'No disponible'}</Text>
                        <Text><strong>Registrador:</strong> {extractRegistrarData(whoisData.entities).registrarName}</Text>
                        <Text><strong>ID IANA del Registrador:</strong> {extractRegistrarData(whoisData.entities).ianaId}</Text>
                        <Text><strong>Contacto de Abuso</strong></Text>
                        <Text><strong>Teléfono:</strong> {extractAbuseContact(whoisData.entities).abusePhone || 'No disponible'}</Text>
                        <Text><strong>Email:</strong> {extractAbuseContact(whoisData.entities).abuseEmail  || 'No disponible'}</Text>
                        <Text><strong>Fecha de Registro:</strong> {whoisData.events && whoisData.events.find(event => event.eventAction === 'registration') ? new Date(whoisData.events.find(event => event.eventAction === 'registration').eventDate).toLocaleDateString() : 'No disponible'}</Text>
                        <Text><strong>Fecha de Expiración:</strong> {whoisData.events && whoisData.events.find(event => event.eventAction === 'expiration') ? new Date(whoisData.events.find(event => event.eventAction === 'expiration').eventDate).toLocaleDateString() : 'No disponible'}</Text>
                        <Text><strong>Nameservers:</strong></Text>
                        <List>
                            {whoisData.nameservers ? whoisData.nameservers.map(ns => (
                                <ListItem key={ns.ldhName}>{ns.ldhName}</ListItem>
                            )) : 'No disponible'}
                        </List>
                    </div>
                )}

                <Text>Tlds soportados: COM, NET, AR, ORG, INFO, BIZ, PRO, BEER, TV, BR, NAME</Text>
            </Container>
        </Body>
    );
}

export default WhoisLookup;
